import React from "react";
import { Link } from "react-router-dom";
import { Snowflake, ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-cyan-300 flex items-center justify-center">
              <Snowflake className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-900">Frostyware 3D</span>
          </Link>
          <Link
            to="/"
            data-testid="back-home-link"
            className="text-sm text-slate-500 hover:text-sky-600 flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-400 mb-10">Last updated: February 22, 2026</p>

        <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Introduction</h2>
            <p>
              Frostyware 3D ("we", "us", or "our") operates the website{" "}
              <a href="https://frostyware3d.com" className="text-sky-600 hover:underline">
                https://frostyware3d.com
              </a>{" "}
              (the "Service"). This page informs you of our policies regarding the
              collection, use, and disclosure of personal data when you use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Information We Collect</h2>
            <p>We collect the following information when you interact with our website:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
              <li>Contact form submissions (name, email, phone, message)</li>
              <li>WhatsApp order inquiries (when you click "Order on WhatsApp")</li>
              <li>Anonymous browsing analytics via Google Analytics (page views, session duration, device type)</li>
              <li>Cookies for advertising personalization (via Google AdSense)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Cookies & Advertising</h2>
            <p>
              We use cookies to improve your experience and to display relevant
              advertisements through <strong>Google AdSense</strong>. Third-party vendors,
              including Google, use cookies to serve ads based on your prior visits to our
              website or other websites on the Internet.
            </p>
            <p className="mt-3">
              You may opt out of personalized advertising by visiting{" "}
              <a
                href="https://www.google.com/settings/ads"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-600 hover:underline"
              >
                Google's Ads Settings
              </a>
              {" "}or by visiting{" "}
              <a
                href="https://www.aboutads.info"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-600 hover:underline"
              >
                www.aboutads.info
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Third-Party Services</h2>
            <p>We use the following third-party services that may collect data about you:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
              <li><strong>Google Analytics</strong> — anonymous traffic analytics</li>
              <li><strong>Google AdSense</strong> — advertising cookies for personalized ads</li>
              <li><strong>WhatsApp</strong> — when you initiate a chat or order via WhatsApp</li>
              <li><strong>Instagram</strong> — when you click our Instagram profile links</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">5. How We Use Your Information</h2>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
              <li>Respond to your inquiries and order requests</li>
              <li>Improve our products, website performance, and user experience</li>
              <li>Serve relevant advertising</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Data Retention</h2>
            <p>
              Contact form messages and order inquiries are retained for as long as is
              necessary to fulfill the purposes outlined in this Privacy Policy. You may
              request deletion of your data by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Your Rights</h2>
            <p>
              You have the right to access, update, or delete the personal information we
              hold about you. To exercise these rights, please contact us via the details
              below.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Children's Privacy</h2>
            <p>
              Our Service is not directed to anyone under the age of 13. We do not
              knowingly collect personally identifiable information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Changes to This Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy on this page with an updated "Last
              updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
              <li>WhatsApp: <a href="https://wa.me/919056382775" className="text-sky-600 hover:underline">+91 90563 82775</a></li>
              <li>Location: Motor Market, Manimajra, Chandigarh, India</li>
              <li>Instagram: <a href="https://www.instagram.com/frostyware/" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">@frostyware</a></li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="border-t border-slate-100 bg-white py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-slate-400">
          © {new Date().getFullYear()} Frostyware 3D. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
